import { useState } from 'react';
import { AlertCircle, Coins, MessageSquare, TrendingUp } from 'lucide-react';
import type { AgentRunRow } from '@command-room/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/stats/StatCard';
import { DailyChart } from '@/components/stats/DailyChart';
import { useUsageStats } from '@/api/queries';
import { formatEur, formatTokens, durationLabel, relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

type Range = '7d' | '30d' | '90d' | 'all';

const RANGES: { value: Range; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

function RangeSelector({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
      {RANGES.map((r) => (
        <button
          key={r.value}
          onClick={() => onChange(r.value)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-medium transition-colors',
            value === r.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function RecentRunsTable({ runs }: { runs: AgentRunRow[] }) {
  if (runs.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No runs recorded yet.
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
        <tr>
          <th className="px-4 py-2">Started</th>
          <th className="px-4 py-2">Status</th>
          <th className="px-4 py-2 text-right">Tokens in</th>
          <th className="px-4 py-2 text-right">Tokens out</th>
          <th className="px-4 py-2 text-right">Duration</th>
          <th className="px-4 py-2 text-right">Cost (USD)</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {runs.map((r) => (
          <tr key={r.run_id} className="hover:bg-muted/20">
            <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
              {relativeTime(r.started_at)}
            </td>
            <td className="px-4 py-2">
              <Badge variant={r.status === 'failed' ? 'destructive' : 'success'}>
                {r.status}
              </Badge>
            </td>
            <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
              {formatTokens(r.tokens_in)}
            </td>
            <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
              {formatTokens(r.tokens_out)}
            </td>
            <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
              {durationLabel(r.duration_ms)}
            </td>
            <td className="px-4 py-2 text-right tabular-nums font-medium">
              ${Number(r.cost_usd).toFixed(4)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AgentStatsTab({ agentName, recentRuns }: { agentName: string; recentRuns: AgentRunRow[] }) {
  const [range, setRange] = useState<Range>('30d');
  const { data } = useUsageStats(range, agentName);

  const totalTokens = data ? data.totals.tokens_in + data.totals.tokens_out : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <RangeSelector value={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          icon={<MessageSquare className="h-4 w-4" />}
          label="Messages"
          value={data?.totals.runs.toLocaleString() ?? '—'}
          sub={data ? `${data.totals.failed} failed` : undefined}
          warn={(data?.totals.failed ?? 0) > 0}
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Tokens in"
          value={formatTokens(data?.totals.tokens_in)}
          sub={data ? `out: ${formatTokens(data.totals.tokens_out)}` : undefined}
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Total tokens"
          value={formatTokens(totalTokens)}
        />
        <StatCard
          icon={<Coins className="h-4 w-4" />}
          label="Cost (EUR)"
          value={formatEur(data?.totals.cost_eur)}
          sub={data ? `≈ $${data.totals.cost_usd.toFixed(4)}` : undefined}
          accent
        />
      </div>

      {data && data.by_day.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Daily cost (EUR)</CardTitle>
          </CardHeader>
          <CardContent>
            <DailyChart days={data.by_day} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <RecentRunsTable runs={recentRuns} />
        </CardContent>
      </Card>

      {data?.totals.runs === 0 && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <AlertCircle className="h-4 w-4" />
          No runs recorded for {agentName} in this period.
        </p>
      )}
    </div>
  );
}
